import { Component } from 'react';
import { Box, Grid } from '@mui/material';
import { appContext } from '../../../AppContext';
import IbssDialog from '../BaseDialog/IbssDialog';
import { Typography } from '@mui/material';
import IbssButton from '../../Buttons/Button/IbssButton';
import IbssInputDropDown from '../../Inputs/SelectList/IbssInputDropDown';
import { Attendee } from '../../DialogLaunchers/BookingPartiesPicker/BookingPartiesPicker';
import { IbssComponent } from '../../Core/BaseComponent/IbssComponent';

class SwapVisitorIdDialog extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            visitorRows: [],
        };
    }

    public componentDidUpdate(prevProps: IProps, prevState: IState): void
    {
        if (prevProps.open !== this.props.open && this.props.visitors) 
        {
            this.setState({
                visitorRows: this.props.visitors.map(visitor => ({
                    id: visitor.recordId || '',
                    name: visitor.name,
                    currentVisitorTypeId: this.props.oldVisitorIdTypes.find(t => t.VisitorTypeId === visitor.visitorTypeId)?.Name || 'None',
                    newVisitorTypeId: 'None',
                }))
            });
        }
    }

    private handleVisitorTypeChange(id: string, value: string): void
    {
        const updatedRows = this.state.visitorRows.map((row) => row.id === id ? { ...row, newVisitorTypeId: value } : row);
        this.setState({ visitorRows: updatedRows });
    }

    private handleCopyFirstRowType(): void 
    {
        if (this.state.visitorRows.length === 0) return;

        const firstTypeId = this.state.visitorRows[0].newVisitorTypeId;
        const updatedRows = this.state.visitorRows.map(row => ({ ...row, newVisitorTypeId: firstTypeId }));
        this.setState({ visitorRows: updatedRows });
    }

    private handleSetAllToNone(): void
    {
        const updatedRows = this.state.visitorRows.map(row => ({ ...row, newVisitorTypeId: 'None' }));
        this.setState({ visitorRows: updatedRows });
    }

    private handleSubmit(): void 
    {
        const updatedVisitors = this.props.visitors.map(visitor => ({
            ...visitor,
            visitorTypeId: this.state.visitorRows.find(row => row.id === visitor.recordId)?.newVisitorTypeId || 'None'
        }));

        this.props.onVisitorIdSwap?.(updatedVisitors);
        this.props.onClose();
    }

    public render(): JSX.Element
    {
        const rows = this.state.visitorRows;
        const visitorTypeOptions = [{ label: this.labels.HubLabelNone, value: 'None' }, ...this.props.newVisitorIdTypes.map(t => ({ label: t.Name, value: t.VisitorTypeId }))];
        return (
            <IbssDialog
                aria-modal="true"
                aria-label="change visitor details modal"
                fullWidth
                maxWidth={'md'}
                open={this.props.open}
                onClose={() => this.props.onClose()}
                header=
                {
                    <>
                        <label className="modal-heading">{this.labels.funcMovingABooking_L}</label>
                    </>
                }
                dialogContent=
                {
                    <>
                        <Typography variant='subtitle1' className="mb-3">{this.labels.funcMovingABookingContainsVisitors_L}</Typography>
                        <Box sx={{ position: 'relative', height: 340, display: 'flex', flexDirection: 'column' }}>

                            {/* Table Header */}
                            <Grid container spacing={2} sx={{ mb: 0, flex: '0 0 auto', borderRadius: 1, py: 1 }}>
                                <Grid item xs={3} >
                                    <Typography variant="subtitle1" className='font-weight-bold'>{this.labels.HubLabelVisitorName}</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="subtitle1" className='font-weight-bold'>{this.labels.funcCurrentVisitorType_S}</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="subtitle1" className='font-weight-bold'>{this.labels.funcNewVisitorType_S}</Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <Typography variant="subtitle1" className='font-weight-bold'>{this.labels.HubLabelAction}</Typography>
                                </Grid>
                            </Grid>
                            <Box sx={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: 340 }}>
                                {rows.map((row, idx) => (
                                    <Grid container key={row.id} spacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="body2">{row.name}</Typography></Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="body2">{row.currentVisitorTypeId}</Typography></Grid>
                                        <Grid item xs={4}>
                                            <IbssInputDropDown
                                                id={`visitor-type-select-${row.id}`}
                                                value={row.newVisitorTypeId}
                                                options={visitorTypeOptions}
                                                onChange={e => this.handleVisitorTypeChange(row.id, e.target.value)}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            {idx === 0 ? (
                                                <Typography sx={{color: 'var(--ui-primary)', cursor: 'pointer', fontSize: 14 }} onClick={() => this.handleCopyFirstRowType()}>{this.labels.funcCopy_S}</Typography>
                                            ) : null}
                                        </Grid>
                                    </Grid>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex',justifyContent: 'flex-end', mt: 1 }}>
                            <Typography sx={{ color: 'var(--ui-primary)', cursor: 'pointer', fontSize: 14 }} onClick={() => this.handleSetAllToNone()}>
                                {this.labels.funcSetAllToNone_S}
                            </Typography>
                        </Box>
                    </>
                }
                footer=
                {
                    <Box className='d-flex w-90' style={{ justifyContent: 'center', gap: '16px' }}>
                        <IbssButton
                            variant="contained"
                            color='primary'
                            onClick={() => this.handleSubmit()}
                        >
                            {this.labels.HubLabelUpdate}
                        </IbssButton>
                    </Box>
                }
            />
        )
    }
}

export default SwapVisitorIdDialog;

export interface IVisitorRow
{
    id: string;
    name: string;
    currentVisitorTypeId: string;
    newVisitorTypeId: string;
}

export interface IProps
{
    onClose: () => void;
    open: boolean;
    visitors: Attendee[];
    newVisitorIdTypes: IVisitorType[]
    oldVisitorIdTypes: IVisitorType[]
    onVisitorIdSwap: (visitors: Attendee[]) => void;
}

export interface IState
{
    visitorRows: IVisitorRow[];
}

export interface IVisitorType
{
    VisitorTypeId: string;
    Name: string
}